/**
 * Toolbar in stile MUI per l'editor TipTap (RF-03a).
 * I comandi agiscono sull'istanza `editor` passata via prop.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useEditorState } from '@tiptap/react';
import { Box, Divider, TextField, ToggleButton, Tooltip, Typography } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TitleIcon from '@mui/icons-material/Title';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ImageIcon from '@mui/icons-material/Image';
import SmartButtonIcon from '@mui/icons-material/SmartButton';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
//--------------------------------------------------------
import { ButtonDialog } from './ButtonDialog';
import { MediaLibraryDialog } from '../MediaLibraryDialog';
import { normalizeButtonHref } from './EmailButton';

/**
 * Misure rapide, pensate sulla larghezza tipica di una newsletter (circa 600px):
 * "Grande" corrisponde alla colonna piena, le altre a mezza e un terzo di colonna.
 */
const IMAGE_WIDTH_PRESETS = [
  { label: 'Piccola', width: 200 },
  { label: 'Media', width: 400 },
  { label: 'Grande', width: 600 },
];

/** Oltre questa larghezza l'immagine sborderebbe comunque dal corpo dell'email. */
const MAX_IMAGE_WIDTH = 1200;

/** Allineamenti offerti. "Giustificato" è escluso: nelle email rende male su schermi stretti. */
const TEXT_ALIGNMENTS = [
  { value: 'left', label: 'Allinea a sinistra', icon: FormatAlignLeftIcon, stateKey: 'alignLeft' },
  { value: 'center', label: 'Centra', icon: FormatAlignCenterIcon, stateKey: 'alignCenter' },
  { value: 'right', label: 'Allinea a destra', icon: FormatAlignRightIcon, stateKey: 'alignRight' },
];

/**
 * @param {{ editor: import('@tiptap/react').Editor|null, disabled?: boolean }} props
 */
export function EditorToolbar({ editor, disabled, entityId }) {
  // Stato del dialog bottone CTA: `values` non nullo = dialog aperto.
  const [buttonDialog, setButtonDialog] = useState({ open: false, values: null, isEdit: false });
  const [mediaOpen, setMediaOpen] = useState(false);

  /**
   * Stato dell'editor letto in modo reattivo.
   *
   * Leggerlo direttamente durante il render non basta: questo componente non si ri-disegna
   * quando cambia solo la selezione, quindi spostando il cursore i pulsanti restavano fermi
   * allo stato precedente — e i controlli dell'immagine sparivano appena si cliccava altrove,
   * per poi non ricomparire più selezionandola di nuovo.
   */
  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (! ed) return null;

      return {
        bold: ed.isActive('bold'),
        italic: ed.isActive('italic'),
        underline: ed.isActive('underline'),
        heading: ed.isActive('heading', { level: 2 }),
        bulletList: ed.isActive('bulletList'),
        orderedList: ed.isActive('orderedList'),
        link: ed.isActive('link'),
        emailButton: ed.isActive('emailButton'),
        imageSelected: ed.isActive('image'),
        imageWidth: ed.getAttributes('image').width ?? '',
        alignLeft: ed.isActive({ textAlign: 'left' }),
        alignCenter: ed.isActive({ textAlign: 'center' }),
        alignRight: ed.isActive({ textAlign: 'right' }),
      };
    },
  });

  if (!editor || !state) return null;

  // Apre il dialog: in modifica se il cursore è su un bottone esistente, altrimenti in inserimento.
  const openButtonDialog = () => {
    const isEdit = editor.isActive('emailButton');
    const attrs = isEdit ? editor.getAttributes('emailButton') : null;
    setButtonDialog({ open: true, isEdit, values: attrs });
  };

  const closeButtonDialog = () => setButtonDialog({ open: false, values: null, isEdit: false });

  const confirmButtonDialog = (values) => {
    const attrs = { ...values, href: normalizeButtonHref(values.href) };
    if (buttonDialog.isEdit) editor.chain().focus().updateEmailButton(attrs).run();
    else editor.chain().focus().setEmailButton(attrs).run();
    closeButtonDialog();
  };

  const removeButton = () => {
    editor.chain().focus().removeEmailButton().run();
    closeButtonDialog();
  };

  const setLink = () => {
    const previous = editor.getAttributes('link').href ?? '';
    // eslint-disable-next-line no-alert
    const input = window.prompt('URL del link', previous);
    if (input === null) return; // annullato
    const url = input.trim();
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    // Normalizza: aggiunge https:// se manca lo schema (altrimenti il link può essere scartato).
    // I placeholder ({{unsubscribe_url}}…) restano com'è: li risolve il BE all'invio.
    const href = /^(https?:\/\/|mailto:|tel:|\{\{)/i.test(url) ? url : `https://${url}`;

    if (editor.state.selection.empty) {
      // Nessun testo selezionato: inserisce l'URL come testo con la marca link (così è visibile).
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href } }] })
        .run();
    } else {
      // Testo selezionato: applica il link alla selezione.
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
  };

  // L'immagine si sceglie dalla libreria (o vi si carica): un URL esterno digitato a mano
  // può sparire o non essere raggiungibile dal client di posta del destinatario.
  const insertImageFromLibrary = (media) => {
    editor.chain().focus().setImage({ src: media.url, alt: media.file_name }).run();
  };

  const btnSx = { border: 'none', '&.Mui-selected': { backgroundColor: '#eeeeef' } };

  // --- Ridimensionamento immagine: attivo solo con un'immagine selezionata ---
  const { imageSelected, imageWidth } = state;

  /**
   * La larghezza viaggia come attributo HTML: è ciò che i client di posta rispettano.
   *
   * `keepFocus` serve al campo numerico: riportare il fuoco nell'editor a ogni carattere
   * digitato lo farebbe uscire dal campo dopo la prima cifra. L'attributo si applica comunque,
   * perché la selezione del nodo vive nello stato dell'editor e non dipende dal fuoco del DOM.
   */
  const setImageWidth = (width, { keepFocus = false } = {}) => {
    // Posizione dell'immagine: aggiornare gli attributi ricrea il nodo e la selezione
    // decadrebbe, facendo sparire questi stessi controlli dopo ogni modifica. Si rimette
    // quindi la selezione dov'era, così si possono provare più misure di seguito.
    const { from } = editor.state.selection;

    const chain = editor.chain();

    if (! keepFocus) {
      chain.focus();
    }

    chain.updateAttributes('image', { width: width || null }).setNodeSelection(from).run();
  };

  /**
   * Applica l'allineamento al blocco corrente.
   *
   * Con un'immagine selezionata la selezione copre il solo nodo immagine: il paragrafo che la
   * contiene resta fuori e `setTextAlign` non troverebbe nulla da allineare. Si allinea quindi
   * il blocco genitore, ripristinando poi la selezione sull'immagine per non far sparire i
   * controlli di dimensione.
   */
  const applyTextAlign = (value) => {
    if (! editor.isActive('image')) {
      editor.chain().focus().setTextAlign(value).run();

      return;
    }

    const { from } = editor.state.selection;

    editor.chain().focus().selectParentNode().setTextAlign(value).setNodeSelection(from).run();
  };

  const handleWidthInput = (event) => {
    const value = parseInt(event.target.value, 10);
    // Valore vuoto o non numerico: si torna alla dimensione originale.
    const width = Number.isFinite(value) && value > 0 ? Math.min(value, MAX_IMAGE_WIDTH) : null;

    setImageWidth(width, { keepFocus: true });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        p: 0.5,
        borderBottom: '1px solid #c4c4c4',
      }}
    >
      <Tooltip title="Grassetto" arrow>
        <span>
          <ToggleButton
            size="small"
            value="bold"
            disabled={disabled}
            selected={state.bold}
            onClick={() => editor.chain().focus().toggleBold().run()}
            sx={btnSx}
          >
            <FormatBoldIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Tooltip title="Corsivo" arrow>
        <span>
          <ToggleButton
            size="small"
            value="italic"
            disabled={disabled}
            selected={state.italic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            sx={btnSx}
          >
            <FormatItalicIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Tooltip title="Sottolineato" arrow>
        <span>
          <ToggleButton
            size="small"
            value="underline"
            disabled={disabled}
            selected={state.underline}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            sx={btnSx}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="Titolo" arrow>
        <span>
          <ToggleButton
            size="small"
            value="heading"
            disabled={disabled}
            selected={state.heading}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            sx={btnSx}
          >
            <TitleIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Tooltip title="Elenco puntato" arrow>
        <span>
          <ToggleButton
            size="small"
            value="bulletList"
            disabled={disabled}
            selected={state.bulletList}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            sx={btnSx}
          >
            <FormatListBulletedIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Tooltip title="Elenco numerato" arrow>
        <span>
          <ToggleButton
            size="small"
            value="orderedList"
            disabled={disabled}
            selected={state.orderedList}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            sx={btnSx}
          >
            <FormatListNumberedIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Allineamento del blocco corrente: paragrafo, titolo o bottone CTA.
          Le immagini sono inline, quindi seguono l'allineamento del paragrafo. */}
      {TEXT_ALIGNMENTS.map(({ value, label, icon: AlignIcon, stateKey }) => (
        <Tooltip key={value} title={label} arrow>
          <span>
            <ToggleButton
              size="small"
              value={`align-${value}`}
              disabled={disabled}
              selected={state[stateKey]}
              onClick={() => applyTextAlign(value)}
              sx={btnSx}
            >
              <AlignIcon fontSize="small" />
            </ToggleButton>
          </span>
        </Tooltip>
      ))}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="Inserisci/modifica link" arrow>
        <span>
          <ToggleButton
            size="small"
            value="link"
            disabled={disabled}
            selected={state.link}
            onClick={setLink}
            sx={btnSx}
          >
            <LinkIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Tooltip title="Rimuovi link" arrow>
        <span>
          <ToggleButton
            size="small"
            value="unlink"
            disabled={disabled || !state.link}
            onClick={() => editor.chain().focus().unsetLink().run()}
            sx={btnSx}
          >
            <LinkOffIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Tooltip title="Inserisci immagine dalla libreria" arrow>
        <span>
          <ToggleButton size="small" value="image" disabled={disabled} onClick={() => setMediaOpen(true)} sx={btnSx}>
            <ImageIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <MediaLibraryDialog
        open={mediaOpen}
        mode="image"
        entityId={entityId}
        onClose={() => setMediaOpen(false)}
        onSelectMedia={insertImageFromLibrary}
      />

      <Tooltip title={state.emailButton ? 'Modifica bottone' : 'Inserisci bottone'} arrow>
        <span>
          <ToggleButton
            size="small"
            value="emailButton"
            disabled={disabled}
            selected={state.emailButton}
            onClick={openButtonDialog}
            sx={btnSx}
          >
            <SmartButtonIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      {/* Separatore tra sezioni: stile e larghezza (90% del corpo) sono fissati nell'editor. */}
      <Tooltip title="Inserisci separatore" arrow>
        <span>
          <ToggleButton
            size="small"
            value="horizontalRule"
            disabled={disabled}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            sx={btnSx}
          >
            <HorizontalRuleIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      {/* Misure dell'immagine: compaiono solo quando ce n'è una selezionata, per non
          ingombrare la barra nel resto del tempo. */}
      {imageSelected && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Typography variant="caption" sx={{ color: '#637381', mr: 0.5 }}>
            Immagine:
          </Typography>

          {IMAGE_WIDTH_PRESETS.map((preset) => (
            <Tooltip key={preset.label} title={`${preset.width} pixel`} arrow>
              <ToggleButton
                size="small"
                value={`img-${preset.width}`}
                disabled={disabled}
                selected={Number(imageWidth) === preset.width}
                onClick={() => setImageWidth(preset.width)}
                sx={{ ...btnSx, textTransform: 'none', px: 1 }}
              >
                {preset.label}
              </ToggleButton>
            </Tooltip>
          ))}

          <Tooltip title="Dimensione originale" arrow>
            <ToggleButton
              size="small"
              value="img-auto"
              disabled={disabled}
              selected={!imageWidth}
              onClick={() => setImageWidth(null)}
              sx={{ ...btnSx, textTransform: 'none', px: 1 }}
            >
              Originale
            </ToggleButton>
          </Tooltip>

          <TextField
            size="small"
            type="number"
            label="px"
            value={imageWidth}
            onChange={handleWidthInput}
            disabled={disabled}
            inputProps={{ min: 1, max: MAX_IMAGE_WIDTH, style: { padding: '6px 8px', width: 56 } }}
            InputLabelProps={{ shrink: true }}
            sx={{ ml: 0.5 }}
          />
        </>
      )}

      <ButtonDialog
        open={buttonDialog.open}
        initialValues={buttonDialog.values}
        isEdit={buttonDialog.isEdit}
        onClose={closeButtonDialog}
        onConfirm={confirmButtonDialog}
        onRemove={removeButton}
      />
    </Box>
  );
}

EditorToolbar.propTypes = {
  editor: PropTypes.object,
  disabled: PropTypes.bool,
  // Entità mittente: serve alla libreria per sapere dove registrare i file caricati.
  entityId: PropTypes.string,
};

export default EditorToolbar;